import React, { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { getAwsUser, awsSignout } from '../api/auth';
import { storage } from '../constants';

export const AuthContext = createContext();

export const AuthConsumer = AuthContext.Consumer;

const initialState = {
  isReady: false,
  isAuthenticated: false,
  firstName: null,
  lastName: null,
  fullName: null,
  email: null,
  initials: null,
  timedOut: false
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_READY':
      return {
        ...state,
        isReady: true
      };
    case 'SET_USER':
      window.sessionStorage.setItem(storage.AP_USER, JSON.stringify(action.payload));
      if (action.payload) {
        action.payload.email = action.payload.email?.toLowerCase();
      }
      return {
        ...state,
        isAuthenticated: true,
        ...action.payload,
        session: action.session,
        timedOut: false
      };
    case 'UPDATE_USER':
      const loggedInUser = window.sessionStorage.getItem(storage.AP_USER);
      const updatedUser = JSON.parse(loggedInUser);
      updatedUser.phoneNumber = action.payload;
      window.sessionStorage.setItem(storage.AP_USER, JSON.stringify(updatedUser));
      return {
        ...state,
        phoneNumber: action.payload
      };
    case 'SIGN_OUT_USER':
      if (state.aws) {
        // Revokes the refresh token and destroys the server session.
        awsSignout().catch(() => {});
      }
      window.sessionStorage.removeItem(storage.AP_USER);
      return {
        ...initialState,
        isReady: true,
        timedOut: action.payload
      };
    default:
      throw new Error('Unknown action type');
  }
};

export const AuthProvider = (props) => {
  const [authState, authDispatch] = useReducer(reducer, initialState);
  const loggedInUser = window.sessionStorage.getItem(storage.AP_USER);

  const setAuthReady = () =>
    authDispatch({
      type: 'SET_READY'
    });

  // On load, a remembered Cognito user is only trusted if the server still
  // has a live Cognito session for this browser. Cognito itself is never
  // contacted from the browser.
  useEffect(() => {
    if (!loggedInUser) {
      setAuthReady();
      return;
    }

    const userObj = JSON.parse(loggedInUser);

    if (!userObj.aws) {
      authDispatch({
        type: 'SET_USER',
        payload: userObj
      });
      setAuthReady();
      return;
    }

    getAwsUser()
      .then((user) => {
        userObj.preferred = user.mfa.preferred;
        userObj.enabled = user.mfa.enabled;
        userObj.phoneNumber = user.attributes.phone_number || userObj.phoneNumber;
        authDispatch({
          type: 'SET_USER',
          payload: userObj,
          session: user
        });
        setAuthReady();
      })
      .catch(() => {
        authDispatch({
          type: 'SIGN_OUT_USER'
        });
        setAuthReady();
      });
  }, [loggedInUser]);

  return (
    <AuthContext.Provider value={[authState, authDispatch]}>
      {props.children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};
